/**
 * Personal KB API client (ADR 0019 §P3 + §P5).
 *
 * Mirrors `apps/backend/app/kbs_api.py` types. The functions here are
 * thin fetch wrappers — they only handle auth headers and the
 * dev-user shim. Business logic lives in the page / component.
 */
import { acquireToken, USE_REAL_AUTH } from "../auth/msal";

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

export interface Kb {
  id: number;
  owner_oid: string;
  name: string;
  description: string;
  is_deleted: boolean;
  doc_count: number;
  byte_count: number;
  created_at: string;
  updated_at: string;
}

export interface KbDocument {
  id: string;
  kb_id: number;
  filename: string;
  byte_count: number;
  status: "pending" | "indexing" | "ready" | "failed";
  error: string | null;
  indexed_at: string | null;
  created_at: string;
}

export interface KbShare {
  kb_id: number;
  grantee_oid: string;
  granted_by: string;
  granted_at: string;
}

export interface KbDetail extends Kb {
  documents: KbDocument[];
  shares: KbShare[];
}

export interface KbUploadResponse {
  doc_id: string;
  kb_id: number;
  filename: string;
  byte_count: number;
  status: "ready" | "pending";
  chunks_indexed: number;
  inline: boolean;
  /** The owner gets a "ready" e-mail when async indexing finishes. */
  notify_by_email?: boolean;
}

async function authHeaders(devUser: string): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  if (USE_REAL_AUTH) {
    const t = await acquireToken();
    if (t) headers["Authorization"] = `Bearer ${t.token}`;
  } else if (devUser) {
    headers["X-Dev-User"] = devUser;
  }
  return headers;
}

async function json<T>(r: Response, action: string): Promise<T> {
  if (!r.ok) {
    let detail = "";
    try {
      const body = await r.json();
      detail = body?.detail ?? JSON.stringify(body);
    } catch {
      detail = await r.text();
    }
    throw new Error(`${action} failed: HTTP ${r.status} — ${detail}`);
  }
  return r.json() as Promise<T>;
}

export async function listKbs(devUser: string): Promise<Kb[]> {
  const r = await fetch(`${API_BASE}/api/kbs`, {
    headers: { ...(await authHeaders(devUser)), Accept: "application/json" },
  });
  return json<Kb[]>(r, "listKbs");
}

export async function getKb(devUser: string, kbId: number): Promise<KbDetail> {
  const r = await fetch(`${API_BASE}/api/kbs/${kbId}`, {
    headers: { ...(await authHeaders(devUser)), Accept: "application/json" },
  });
  return json<KbDetail>(r, "getKb");
}

export async function createKb(
  devUser: string,
  body: { name: string; description?: string },
): Promise<Kb> {
  const r = await fetch(`${API_BASE}/api/kbs`, {
    method: "POST",
    headers: {
      ...(await authHeaders(devUser)),
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(body),
  });
  return json<Kb>(r, "createKb");
}

export async function deleteKb(devUser: string, kbId: number): Promise<void> {
  const r = await fetch(`${API_BASE}/api/kbs/${kbId}`, {
    method: "DELETE",
    headers: await authHeaders(devUser),
  });
  if (!r.ok && r.status !== 204) {
    throw new Error(`deleteKb failed: HTTP ${r.status}`);
  }
}

/** Which OCR engine leads. `fast` is Document Intelligence (managed, no
 * cold start); `thorough` is docling on the GPU, which scales up from zero
 * before it can start. Each falls back to the other. */
export type KbUploadMode = "fast" | "thorough";

export async function uploadKbDocument(
  devUser: string,
  kbId: number,
  file: File,
  opts: { mode?: KbUploadMode; relativePath?: string } = {},
): Promise<KbUploadResponse> {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("mode", opts.mode ?? "fast");
  // Where the file sat inside an uploaded folder, so the doc list can show
  // provenance instead of a flat heap of basenames.
  if (opts.relativePath) fd.append("relative_path", opts.relativePath);
  const r = await fetch(`${API_BASE}/api/kbs/${kbId}/documents`, {
    method: "POST",
    headers: await authHeaders(devUser),
    body: fd,
  });
  return json<KbUploadResponse>(r, "uploadKbDocument");
}

export async function deleteKbDocument(
  devUser: string,
  kbId: number,
  docId: string,
): Promise<void> {
  const r = await fetch(
    `${API_BASE}/api/kbs/${kbId}/documents/${encodeURIComponent(docId)}`,
    {
      method: "DELETE",
      headers: await authHeaders(devUser),
    },
  );
  if (!r.ok && r.status !== 204) {
    throw new Error(`deleteKbDocument failed: HTTP ${r.status}`);
  }
}

export async function shareKb(
  devUser: string,
  kbId: number,
  grantee: { oid?: string; email?: string },
): Promise<KbShare> {
  const body: Record<string, string> = {};
  if (grantee.email) body.grantee_email = grantee.email;
  if (grantee.oid) body.grantee_oid = grantee.oid;
  const r = await fetch(`${API_BASE}/api/kbs/${kbId}/shares`, {
    method: "POST",
    headers: {
      ...(await authHeaders(devUser)),
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(body),
  });
  return json<KbShare>(r, "shareKb");
}

export async function revokeKbShare(
  devUser: string,
  kbId: number,
  granteeOid: string,
): Promise<void> {
  const r = await fetch(
    `${API_BASE}/api/kbs/${kbId}/shares/${encodeURIComponent(granteeOid)}`,
    {
      method: "DELETE",
      headers: await authHeaders(devUser),
    },
  );
  if (!r.ok && r.status !== 204) {
    throw new Error(`revokeKbShare failed: HTTP ${r.status}`);
  }
}

/** Replace a conversation's selected KB ids — the KbPicker writes
 * here. Empty array clears the selection. */
export async function setConversationKbIds(
  devUser: string,
  conversationId: number,
  kbIds: number[],
): Promise<void> {
  const r = await fetch(
    `${API_BASE}/api/conversations/${conversationId}/kb_ids`,
    {
      method: "PATCH",
      headers: {
        ...(await authHeaders(devUser)),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ kb_ids: kbIds }),
    },
  );
  if (!r.ok && r.status !== 204) {
    throw new Error(`setConversationKbIds failed: HTTP ${r.status}`);
  }
}
